import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { useConfirmationModal } from "../app/confirmation";
import { CustomerModel } from "../../api/customers/CustomerModel";
import { CustomerAdapter } from "../../api/customers/CustomerAdapter";
import type {
  ZekayCustomer,
  CustomersPaginationRequest,
  SearchCustomersRequest,
} from "../../api/customers/Customer.typed";
import type { Excursion } from "../../api/excursions/Excursion.typed";
import type { PaginatedResponse } from "../../api/zekay/pagination";
interface CustomerContextType {
  getCustomers: (
    paginationParams?: CustomersPaginationRequest,
  ) => Promise<PaginatedResponse<ZekayCustomer> | ZekayCustomer[] | undefined>;
  getCustomer: (id: string) => Promise<ZekayCustomer | undefined>;
  getCustomersBatch: (ids: number[]) => Promise<ZekayCustomer[] | undefined>;
  createCustomer: (payload: ZekayCustomer) => Promise<ZekayCustomer | void>;
  updateCustomer: (id: number, payload: ZekayCustomer) => Promise<ZekayCustomer | undefined>;
  deleteCustomer: (id: number) => Promise<void>;
  searchCustomers: (searchRequest: SearchCustomersRequest) => Promise<ZekayCustomer[] | undefined>;
  updateFile: (formData: FormData, internalCustomerId?: number) => Promise<void>;
  deleteCustomerFile: (payload: { CustomerID: number; Type: "photo" | "medical" }) => Promise<void>;
  /** Client-side guard for actions that require a customer id (toast + false if missing). */
  warnIfMissingCustomerId: (customerId: number | undefined) => customerId is number;
  getCustomerExcursions: (
    customerId: number,
    page?: number,
    limit?: number,
  ) => Promise<PaginatedResponse<Excursion> | undefined>;
}

const CustomerContext = React.createContext<CustomerContextType>(null!);

export function CustomerProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new CustomerModel(appConfig);
    return new CustomerAdapter(model);
  }, [appConfig]);

  const warnIfMissingCustomerId = React.useCallback(
    (customerId: number | undefined): customerId is number => {
      if (customerId == null || !Number.isFinite(Number(customerId))) {
        showAlert("ID client manquant", "warning");
        return false;
      }
      return true;
    },
    [showAlert],
  );

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>, successMsg?: string): Promise<T | undefined> => {
      try {
        const result = await fn();
        if (successMsg) showAlert(successMsg, "success");
        return result;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const getCustomers = React.useCallback(
    async (
      paginationParams?: CustomersPaginationRequest,
    ): Promise<PaginatedResponse<ZekayCustomer> | ZekayCustomer[] | undefined> =>
      adapter ? wrap(() => adapter.getCustomers(paginationParams)) : undefined,
    [adapter, wrap],
  );

  const getCustomer = React.useCallback(
    async (id: string): Promise<ZekayCustomer | undefined> =>
      adapter ? wrap(() => adapter.getCustomer(id)) : undefined,
    [adapter, wrap],
  );

  const getCustomersBatch = React.useCallback(
    async (ids: number[]): Promise<ZekayCustomer[] | undefined> =>
      adapter ? wrap(() => adapter.getCustomersBatch(ids)) : undefined,
    [adapter, wrap],
  );

  const createCustomer = React.useCallback(
    async (payload: ZekayCustomer): Promise<ZekayCustomer | void> =>
      adapter ? wrap(() => adapter.createCustomer(payload), "Client créé avec succès") : undefined,
    [adapter, wrap],
  );

  const updateCustomer = React.useCallback(
    async (id: number, payload: ZekayCustomer): Promise<ZekayCustomer | undefined> => {
      if (!adapter) return undefined;
      try {
        const updated = await adapter.updateCustomer(id, payload);
        showAlert("Client mis à jour", "success");
        return updated;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [adapter, showAlert],
  );

  const deleteCustomer = React.useCallback(
    async (id: number): Promise<void> => {
      if (!adapter) return;
      const ok = await showConfirmation(
        "Supprimer le client",
        "Êtes-vous sûr de vouloir supprimer ce client ?",
      );
      if (!ok) return;
      try {
        await adapter.deleteCustomer(id);
        showAlert("Client supprimé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const searchCustomers = React.useCallback(
    async (searchRequest: SearchCustomersRequest): Promise<ZekayCustomer[] | undefined> =>
      adapter ? wrap(() => adapter.searchCustomers(searchRequest)) : undefined,
    [adapter, wrap],
  );

  const updateFile = React.useCallback(
    async (formData: FormData, _internalCustomerId?: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateFile(formData);
        showAlert("Fichier mis à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const deleteCustomerFile = React.useCallback(
    async (payload: { CustomerID: number; Type: "photo" | "medical" }): Promise<void> => {
      if (!adapter) return;
      const ok = await showConfirmation(
        "Supprimer le fichier",
        "Êtes-vous sûr de vouloir supprimer ce fichier ?",
      );
      if (!ok) return;
      try {
        await adapter.deleteCustomerFile(payload);
        showAlert("Fichier supprimé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const getCustomerExcursions = React.useCallback(
    async (
      customerId: number,
      page?: number,
      limit?: number,
    ): Promise<PaginatedResponse<Excursion> | undefined> =>
      adapter ? wrap(() => adapter.getCustomerExcursions(customerId, page, limit)) : undefined,
    [adapter, wrap],
  );

  const value = React.useMemo<CustomerContextType>(
    () => ({
      getCustomers,
      getCustomer,
      getCustomersBatch,
      createCustomer,
      updateCustomer,
      deleteCustomer,
      searchCustomers,
      updateFile,
      deleteCustomerFile,
      warnIfMissingCustomerId,
      getCustomerExcursions,
    }),
    [
      getCustomers,
      getCustomer,
      getCustomersBatch,
      createCustomer,
      updateCustomer,
      deleteCustomer,
      searchCustomers,
      updateFile,
      deleteCustomerFile,
      warnIfMissingCustomerId,
      getCustomerExcursions,
    ],
  );

  return <CustomerContext.Provider value={value}>{props.children}</CustomerContext.Provider>;
}

export function useCustomer(): CustomerContextType {
  const ctx = React.useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomer must be used within CustomerProvider");
  return ctx;
}
